import * as React from 'react'
import {ContentAreas, type ContentAreaT} from '@frontrow/console/src/entities/helpers/content-area'
import {exhaustive} from '@freckle/exhaustive'
import Input from '@frontrow/console/src/entities/components/base/input'
import {any} from '@freckle/parser'
import {Button} from '@frontrow/console/src/components/button'
import {H2, H3} from '@frontrow/console/src/components/typography'
import {
  GenericSelector,
  createSelectorOptionAsItem
} from '@frontrow/console/src/components/generic-selector'
import {Dialog, DialogDismiss, DialogHeading} from '@ariakit/react'
import * as dialogStyles from '@frontrow/console/src/components/dialog/dialog.module.scss'
import {type OperationLocationT} from '@frontrow/console/src/entities/helpers/operation'
import {startOperation} from '@frontrow/console/src/entities/helpers/operation'
import {SpinnerOverlay} from '@frontrow/console/src/components/spinner'
import {type RlStandardSetT} from '@frontrow/console/src/entities/models/rl-standard-set'
import {useSafeCallback} from '@freckle/react-hooks'
import CApiHelper from '@frontrow/console/src/entities/helpers/common-api-helper'
import {PendingOperationWhenLocation} from '@frontrow/console/src/components/pending-operation'
import ErrorAlert from '@frontrow/console/src/components/common/error-alert'

type Props = {
  mathStandardSets: RlStandardSetT[]
  elaStandardSets: RlStandardSetT[]
}

type RoleSelection = 'teachers' | 'teachers_and_school_admins'

type State = {
  contentArea: ContentAreaT
  role: RoleSelection
  schoolId: number | null
  sourceStandardSetId: string | null
  administrativeArea: string | null
  destinationStandardSetId: string | null
  targetBy: {
    school: boolean
    standardSet: boolean
    administrativeArea: boolean
  }
}

type TargetField = 'school' | 'standard-set' | 'administrative-area'

type Action =
  | {
      tag: 'set-content-area'
      selectedContentArea: ContentAreaT
    }
  | {
      tag: 'set-roles'
      roleSelection: RoleSelection
    }
  | {
      tag: 'toggle-target-by'
      field: TargetField
    }
  | {
      tag: 'update-school-id'
      rawValue: string
    }
  | {
      tag: 'set-source-standard-set'
      selectedStandardSetId: string | null
    }
  | {
      tag: 'set-destination-standard-set'
      selectedStandardSetId: string | null
    }
  | {
      tag: 'update-administrative-area'
      rawValue: string
    }

const roles = (
  contentArea: ContentAreaT
): {
  default: RoleSelection
  available: RoleSelection[]
} => {
  switch (contentArea) {
    case 'math':
      return {
        default: 'teachers',
        available: ['teachers', 'teachers_and_school_admins']
      }
    case 'ela':
      return {default: 'teachers', available: ['teachers']}
    default:
      return exhaustive(contentArea)
  }
}

const initialState: State = {
  contentArea: ContentAreas.math,
  role: roles(ContentAreas.math).default,
  schoolId: null,
  sourceStandardSetId: null,
  administrativeArea: null,
  destinationStandardSetId: null,
  targetBy: {
    school: false,
    standardSet: false,
    administrativeArea: false
  }
}

function reducer(state: State, action: Action): State {
  switch (action.tag) {
    case 'set-content-area':
      return {
        ...state,
        contentArea: action.selectedContentArea,
        role: roles(action.selectedContentArea).default,
        sourceStandardSetId: null,
        destinationStandardSetId: null,
        targetBy: {
          school: false,
          standardSet: false,
          administrativeArea: false
        }
      }
    case 'set-roles':
      return {
        ...state,
        role: action.roleSelection
      }
    case 'toggle-target-by':
      switch (action.field) {
        case 'school':
          return {
            ...state,
            targetBy: {...state.targetBy, school: !state.targetBy.school}
          }
        case 'standard-set':
          return {
            ...state,
            targetBy: {
              ...state.targetBy,
              standardSet: !state.targetBy.standardSet
            }
          }
        case 'administrative-area':
          return {
            ...state,
            targetBy: {
              ...state.targetBy,
              administrativeArea: !state.targetBy.administrativeArea
            }
          }
        default:
          return exhaustive(action.field)
      }
    case 'update-school-id': {
      const parsed = parseInt(action.rawValue, 10)

      return {...state, schoolId: isNaN(parsed) ? null : parsed}
    }
    case 'update-administrative-area': {
      return {...state, administrativeArea: action.rawValue}
    }
    case 'set-source-standard-set':
      return {...state, sourceStandardSetId: action.selectedStandardSetId}
    case 'set-destination-standard-set':
      return {
        ...state,
        destinationStandardSetId: action.selectedStandardSetId
      }
    default:
      return exhaustive(action as never)
  }
}

const standardSets = (
  contentArea: ContentAreaT,
  {mathStandardSets, elaStandardSets}: Props
): RlStandardSetT[] => {
  switch (contentArea) {
    case 'math':
      return mathStandardSets
    case 'ela':
      return elaStandardSets
    default:
      return exhaustive(contentArea)
  }
}

const contentAreaLabel = (contentArea: ContentAreaT): string => {
  switch (contentArea) {
    case 'math':
      return 'Math'
    case 'ela':
      return 'ELA'
    default:
      return exhaustive(contentArea)
  }
}

const roleLabel = (role: RoleSelection): string => {
  switch (role) {
    case 'teachers':
      return 'Teachers'
    case 'teachers_and_school_admins':
      return 'Teachers & School Admins'
    default:
      return exhaustive(role)
  }
}

const errorsFromState = ({
  schoolId,
  sourceStandardSetId,
  administrativeArea,
  destinationStandardSetId,
  targetBy
}: State): string[] => [
  ...(targetBy.school && schoolId !== null && schoolId < 1
    ? ['School ID must be greater than 1']
    : []),
  ...(targetBy.standardSet && sourceStandardSetId === destinationStandardSetId
    ? ['Standard sets must differ']
    : []),
  ...(targetBy.standardSet && sourceStandardSetId === null
    ? ['Current standard set is required if targeting by standard set']
    : []),
  ...(targetBy.school && schoolId === null
    ? ['School ID is required if targeting by school ID']
    : []),
  ...(targetBy.administrativeArea &&
  (administrativeArea === null || administrativeArea.match(/\s/g))
    ? ['Administrative area is required if targeting by administrative area']
    : []),
  ...(destinationStandardSetId === null ? ['A destination standard set must be selected'] : []),
  ...(!targetBy.school && !targetBy.standardSet && !targetBy.administrativeArea
    ? ['At least one targeting parameter must be selected']
    : [])
]

type Mode =
  | {
      tag: 'form'
    }
  | {
      tag: 'modal'
    }
  | {
      tag: 'operation-starting'
    }
  | {
      tag: 'operation-pending'
      location: OperationLocationT
    }
  | {
      tag: 'unexpected-failure'
      message: string
    }
  | {
      tag: 'success'
    }

// N.B. assumes validation/field-coherence-checks happens elsewhere
const toRequest = ({
  destinationStandardSetId: toStandardSetId,
  role: forRole,
  targetBy,
  sourceStandardSetId: fromStandardSetId,
  schoolId: inSchoolId,
  administrativeArea: inAdministrativeArea
}: State): {
  toStandardSetId: string
  forRole: RoleSelection
  fromStandardSetId?: string
  inSchoolId?: number
  inAdministrativeArea?: string
} => ({
  toStandardSetId: toStandardSetId ?? '',
  forRole,
  ...(targetBy.standardSet && fromStandardSetId !== null ? {fromStandardSetId} : {}),
  ...(targetBy.school && inSchoolId !== null ? {inSchoolId} : {}),
  ...(targetBy.administrativeArea && inAdministrativeArea !== null ? {inAdministrativeArea} : {})
})

export function StandardSetsPage(props: Props): React.ReactElement {
  const [state, dispatch] = React.useReducer(reducer, initialState)
  const [mode, setMode] = React.useState<Mode>({tag: 'form'})
  const onOperationSuccess = useSafeCallback(() => () => setMode({tag: 'success'}), [])
  const onOperationFailure = useSafeCallback(
    () => () =>
      setMode({
        tag: 'unexpected-failure',
        message: 'An unexpected failure occurred while running the operation'
      }),
    []
  )

  const {
    contentArea,
    role,
    targetBy,
    schoolId,
    sourceStandardSetId,
    administrativeArea,
    destinationStandardSetId
  } = state

  const availableRoles = roles(contentArea).available
  const availableStandardSets = [
    createSelectorOptionAsItem(null, '(None Selected)'),
    ...standardSets(contentArea, props).map(ss =>
      createSelectorOptionAsItem(ss.id, `${ss.description} (${ss.id})`)
    )
  ]

  const errors = errorsFromState(state)

  const onConfirm = () => {
    setMode({tag: 'operation-starting'})
    startOperation(
      'PATCH',
      CApiHelper.fancyPaths.v3.content_area.standard_sets(contentArea),
      any(),
      JSON.stringify(toRequest(state))
    ).then(
      ({operationLocation}) => {
        setMode({tag: 'operation-pending', location: operationLocation})
      },
      () => {
        setMode({
          tag: 'unexpected-failure',
          message: 'An unexpected failure occurred while triggering the operation'
        })
      }
    )
  }

  if (mode.tag === 'operation-starting') {
    return <SpinnerOverlay />
  } else if (mode.tag === 'operation-pending') {
    return (
      <PendingOperationWhenLocation
        operationLocation={mode.location}
        onSuccess={onOperationSuccess}
        onFailure={onOperationFailure}
        title="Standard set update"
        heading="Update pending"
        body={`Please wait while the ${contentAreaLabel(
          contentArea
        )} standard sets are being updated for ${roleLabel(role)}`}
      />
    )
  } else if (mode.tag === 'success') {
    return <H2>Update success!</H2>
  }

  return (
    <>
      <p>This page can be used to perform mass updates on standard set selections.</p>
      {mode.tag === 'unexpected-failure' && <ErrorAlert>{mode.message}</ErrorAlert>}
      <H2>Required Fields</H2>
      <label htmlFor="content-area-selector">Content Area</label>
      <GenericSelector
        id="content-area-selector"
        onChangeSelection={selectedContentArea =>
          dispatch({tag: 'set-content-area', selectedContentArea})
        }
        selection={contentArea}
        collection={[ContentAreas.math, ContentAreas.ela].map(ca =>
          createSelectorOptionAsItem(ca, contentAreaLabel(ca))
        )}
      />
      <label htmlFor="roles-selector">Roles</label>&nbsp;
      {availableRoles.length === 1 && (
        <>
          <i>
            ({contentAreaLabel(contentArea)} standard sets can only be updated for {roleLabel(role)}
            )
          </i>
        </>
      )}
      <GenericSelector
        id="roles-selector"
        onChangeSelection={roleSelection => dispatch({tag: 'set-roles', roleSelection})}
        selection={role}
        disabled={availableRoles.length === 1}
        collection={availableRoles.map(r => createSelectorOptionAsItem(r, roleLabel(r)))}
      />
      <label htmlFor="new-standard-set-id">
        Destination Standard Set (users will be set to this)
      </label>
      <GenericSelector
        id="new-standard-set-id"
        onChangeSelection={selectedStandardSetId =>
          dispatch({
            tag: 'set-destination-standard-set',
            selectedStandardSetId
          })
        }
        selection={destinationStandardSetId}
        collection={availableStandardSets}
      />
      <H2>Targeting</H2>
      <i>At least one must be selected.</i>
      <p>
        <b>These selections dictate which {roleLabel(role)} will be updated</b>
      </p>
      <Input
        id="target-by-school-checkbox"
        type="checkbox"
        checked={targetBy.school}
        onChange={() => dispatch({tag: 'toggle-target-by', field: 'school'})}
      />{' '}
      &nbsp;
      <label htmlFor="target-by-school-checkbox">Target by school ID</label>
      &nbsp;
      {targetBy.school && (
        <input
          id="school-id"
          value={schoolId ?? ''}
          type="number"
          min={1}
          onChange={event => dispatch({tag: 'update-school-id', rawValue: event.target.value})}
        />
      )}
      <br />
      <Input
        id="target-by-standard-set-checkbox"
        type="checkbox"
        checked={targetBy.standardSet}
        onChange={() => dispatch({tag: 'toggle-target-by', field: 'standard-set'})}
      />{' '}
      &nbsp;
      <label htmlFor="target-by-standard-set-checkbox">Target by current Standard Set</label>
      {targetBy.standardSet && (
        <GenericSelector
          id="target-standard-set-id"
          onChangeSelection={selectedStandardSetId =>
            dispatch({tag: 'set-source-standard-set', selectedStandardSetId})
          }
          selection={sourceStandardSetId}
          collection={availableStandardSets}
        />
      )}
      <br />
      <Input
        id="target-by-administrative-area-checkbox"
        type="checkbox"
        checked={targetBy.administrativeArea}
        onChange={() => dispatch({tag: 'toggle-target-by', field: 'administrative-area'})}
      />{' '}
      &nbsp;
      <label htmlFor="target-by-administrative-area-checkbox">Target by Administrative Area</label>
      &nbsp;
      {targetBy.administrativeArea && (
        <input
          id="administrative-area"
          value={administrativeArea ?? ''}
          type="text"
          onChange={event =>
            dispatch({
              tag: 'update-administrative-area',
              rawValue: event.target.value
            })
          }
        />
      )}
      <br />
      <H2>Summary</H2>
      {errors.length > 0 && (
        <>
          <H3>Errors</H3>
          <ul>
            {errors.map(error => (
              <li key={error}>{error}</li>
            ))}
          </ul>
        </>
      )}
      {errors.length === 0 && <Summary {...state} />}
      <br />
      <Button disabled={errors.length > 0} onClick={() => setMode({tag: 'modal'})}>
        Run Update
      </Button>
      <Dialog
        open={mode.tag === 'modal'}
        onClose={() => setMode({tag: 'form'})}
        backdrop={<div className={dialogStyles.backdrop} />}
        className={`${dialogStyles.dialog} ${dialogStyles.dialogSm}`}
        portal
        unmountOnHide
        hideOnEscape
        hideOnInteractOutside={false}
      >
        <div className={dialogStyles.header}>
          <DialogHeading className={dialogStyles.title}>Confirm standard set update</DialogHeading>
          <DialogDismiss className={dialogStyles.closeButton}>
            <span aria-hidden="true">&times;</span>
          </DialogDismiss>
        </div>
        <div className={dialogStyles.body}>
          <Summary {...state} />
        </div>
        <div className={dialogStyles.footer}>
          <Button style="tertiary" onClick={() => setMode({tag: 'form'})}>
            Cancel
          </Button>
          <Button style="primary" onClick={onConfirm}>
            Yes, run update
          </Button>
        </div>
      </Dialog>
    </>
  )
}

const Summary = ({
  contentArea,
  role,
  targetBy,
  schoolId,
  sourceStandardSetId,
  administrativeArea,
  destinationStandardSetId
}: State): React.ReactElement => (
  <>
    <b>{roleLabel(role)}</b> with
    <ul>
      {targetBy.school && (
        <li>
          School ID = <b>"{schoolId}"</b>
          {(targetBy.standardSet || targetBy.administrativeArea) && <>, AND</>}
        </li>
      )}
      {targetBy.standardSet && (
        <li>
          Current {contentAreaLabel(contentArea)} standard set = <b>"{sourceStandardSetId}"</b>
          {targetBy.administrativeArea && <>, AND</>}
        </li>
      )}
      {targetBy.administrativeArea && (
        <li>
          Administrative area = <b>"{administrativeArea}"</b>
        </li>
      )}
    </ul>
    will have their {contentAreaLabel(contentArea)} standard set updated to{' '}
    <b>"{destinationStandardSetId}"</b>
  </>
)
